import { types } from "mobx-state-tree";
import { ListOfItemsModel } from "../../mobxDataModels/listOfItemsModel";
import { v4 as uuidv4 } from "uuid";

export const Mk8sDraftAddonRegistryMirrorItemModel = types
  .model({
    id: types.optional(types.string, () => uuidv4()),
    _registry: types.optional(types.string, ""),
    _mirrors: types.array(types.string),
    registry: types.optional(types.string, ""),
    mirrors: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
  })
  .actions((self) => ({
    reset() {
      self.registry = self._registry;
      self.mirrors = ListOfItemsModel.create({ _items: (self._mirrors || []).map((i) => ({ firstValue: i })) });
    },
    confirm() {
      self._registry = self.registry;
      self._mirrors.clear();
      for (const mirror of [...self.mirrors.items.map((i) => i.firstValue)]) {
        self._mirrors.push(mirror);
      }
    },
    setRegistry(value: string) {
      self.registry = value;
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();

      if (self.mirrors.items.length < 1) {
        self.mirrors.add();
      }
    },
  }))
  .views((self) => ({
    get asObject() {
      return {
        registry: self.registry,
        mirrors: self.mirrors.items.filter((i) => !!i.firstValue).map((i) => i.firstValue),
      };
    },
  }));

export const Mk8sDraftAddonRegistryMirrorModel = types
  .model({
    _isEnabled: types.optional(types.boolean, false),
    isEnabled: types.optional(types.boolean, false),
    _mirrors: types.frozen(),
    mirrors: types.array(Mk8sDraftAddonRegistryMirrorItemModel),
  })
  .actions((self) => ({
    setIsEnabled(value: boolean) {
      self.isEnabled = value;
    },
    addMirror() {
      self.mirrors.push(Mk8sDraftAddonRegistryMirrorItemModel.create());
    },
    removeMirror(id: string) {
      const item = self.mirrors.find((m) => m.id === id);
      if (item) {
        self.mirrors.remove(item);
      }
    },
  }))
  .actions((self) => ({
    reset() {
      self.isEnabled = self._isEnabled;

      self.mirrors.clear();
      for (const mirror of self._mirrors || []) {
        const item = Mk8sDraftAddonRegistryMirrorItemModel.create({
          _mirrors: mirror.mirrors || [],
          _registry: mirror.registry || "",
        });
        self.mirrors.push(item);
      }
    },
    confirm() {
      self._isEnabled = self.isEnabled;
      self._mirrors = self.mirrors.map((m) => m.asObject);
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();
    },
  }))
  .views((self) => ({
    get isDirty() {
      if (self.isEnabled !== self._isEnabled) {
        return true;
      }

      const newMirrors = [...self.mirrors.map((m) => m.asObject)];
      const mirrors = self._mirrors || [];
      if (mirrors.length !== newMirrors.length) {
        return true;
      }

      for (const mirror of mirrors) {
        const newMirror = newMirrors.find((nm) => nm.registry === mirror.registry);
        if (!newMirror) {
          return true;
        }
        if (newMirror.mirrors.length !== mirror.mirrors.length) {
          return true;
        }
        for (const m of mirror.mirrors) {
          if (!newMirror.mirrors.includes(m)) {
            return true;
          }
        }
        for (const m of newMirror.mirrors) {
          if (!mirror.mirrors.includes(m)) {
            return true;
          }
        }
      }

      for (const newMirror of newMirrors) {
        const mirror = mirrors.find((nm) => nm.registry === newMirror.registry);
        if (!mirror) {
          return true;
        }
        if (mirror.mirrors.length !== newMirror.mirrors.length) {
          return true;
        }
        for (const m of newMirror.mirrors) {
          if (!mirror.mirrors.includes(m)) {
            return true;
          }
        }
        for (const m of mirror.mirrors) {
          if (!newMirror.mirrors.includes(m)) {
            return true;
          }
        }
      }

      return false;
    },
    get asObject() {
      let res: any = {
        mirrors: self.mirrors.map((m) => m.asObject),
      };
      return res;
    },
  }));
